import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Grid, Typography } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch } from "react-redux";
// icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// utils and helpers
import { openSnackbar } from "../../redux/slices/common";
// custom components
import NoDataFound from "common/component/NoDataFound";
import StatusText from "common/component/StatusText";

export const TableLoader = () => {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
      <CircularProgress size={32} />
    </div>
  )
}

const defaultPage = {
  page: 1,
  limit: 20
}

const GridRow = React.memo(({ data, columns }) => {
  return (
    <Box sx={{ width: "100%" }} className="accordion-header">
      {
        columns?.map((column, index) => {
          const cellData = column.cellRenderer ? column.cellRenderer({ data, colDef: column }) : column.valueGetter ? column.valueGetter({ data }) : data[column.field] || "";

          return (
            column.field === "status"
              ? <Typography key={column.field || index} className="field-region">
                <StatusText status={data?.status ? "Active" : "Inactive"} />
              </Typography>
              : <Typography key={column.field || index} className="field-region">
                {cellData}
              </Typography>
          )
        })
      }
    </Box>
  )
});

const AgGridAccordionChildScrollingTable = ({
  tableName,
  columnDefs,
  expandedColumnDefs,
  refreshTable,
  getDataMethod = () => { },
  customFilters = {},
  dataList = [],
  showMainHeader = false,
  onSelectionChange = () => { }
}) => {
  const dispatch = useDispatch()

  const [expanded, setExpanded] = useState("");
  const [paginationDetails, setPaginationDetails] = useState({ ...defaultPage });
  const [hasMore, setHasMore] = useState(true);
  const [childrenList, setChildrenList] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const handleExpandRegion = useCallback((panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : "");
    setPaginationDetails({ ...defaultPage })
    setHasMore(true)
    setChildrenList({ data: [], total: 0 })
    setIsLoading(true);
  }, []);

  const getHeight = useCallback((childrenLength) => !childrenLength ? 150 : Math.min((childrenLength * 56) + 39, 350), []);

  const getTableSpecificData = useCallback((data) => {
    let childrenLength = 1;

    switch (tableName) {
      case "Category Products":
        childrenLength = data?.products?.length || 0;
        return {
          childrenLength,
          rowId: data?._id,
          childrenTableHeight: getHeight(childrenLength)
        };
      case "Box Products":
        return {
          childrenLength,
          rowId: data?.boxId,
          childrenTableHeight: getHeight(childrenLength)
        };
      case "Aging Products":
        return {
          childrenLength,
          rowId: data?.id,
          childrenTableHeight: getHeight(childrenLength)
        };
      default:
        return {
          childrenLength,
          rowId: "",
          childrenTableHeight: getHeight(childrenLength)
        };
    }
  }, [getHeight, tableName]);

  const handleDataList = useCallback(async (filters = {}) => {
    try {
      if (!getDataMethod) return;

      const request = { ...customFilters, ...paginationDetails, ...filters };
      if (tableName === "Aging Products") {
        request.categoryId = expanded
      }
      if (tableName === "Box Products") {
        request.boxId = expanded
      }

      const response = await dispatch(getDataMethod(request)).unwrap();
      const data = response?.data || response?.products

      if (data) {
        let myData = {};
        if (request.page === 1) {
          myData = { ...response, data }
        } else {
          myData = { ...response, data: [...(childrenList?.data || []), ...data] }
        }

        setChildrenList(myData);

        const hasMoreData = response?.total > (myData?.data?.length || 0);
        if (hasMoreData) {
          setPaginationDetails(prev => ({ ...prev, page: prev.page + 1 }));
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error;
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    } finally {
      setIsLoading(false);
    }
  }, [customFilters, dispatch, expanded, getDataMethod, paginationDetails, childrenList?.data]);

  const handleSelectAll = (e, parentId) => {
    const { checked } = e.target;

    setIsSelectedAll(checked);
    setSelectedRows([]);
    onSelectionChange({ isSelectedAll: checked, selectedRows: [], parentId });
  };

  const handleRowSelection = (e, rowId, parentId, rowData) => {
    const { checked } = e.target;

    // If unchecking, set select all to false
    if (!checked) {
      setIsSelectedAll(false);
    }

    setSelectedRows(prev => {
      let updatedRows = prev.filter(row => row.rowId !== rowId);

      if (checked) {
        updatedRows.push({ rowId, rowData });
      } else if (!checked && isSelectedAll) {
        // If select all was previously true and a row is being unchecked,
        // deselect only that row and keep the rest selected
        const checkboxColumn = memoizedExpandedColumnDefs?.find(col => col.checkboxSelection);
        if (checkboxColumn && childrenList?.data) {
          updatedRows = childrenList.data
            .map(row => ({ rowData: row, rowId: checkboxColumn.setRowId({ data: row }) }))
            .filter(row => row.rowId !== rowId);
        }
      }

      // Notify parent of selection change
      onSelectionChange({ isSelectedAll: false, selectedRows: updatedRows, parentId });
      return updatedRows;
    });
  };

  useEffect(() => {
    if (expanded) {
      handleDataList({ ...defaultPage })
    }
  }, [expanded, refreshTable]);

  const memoizedColumnDefs = useMemo(() => columnDefs, [columnDefs]);
  const memoizedExpandedColumnDefs = useMemo(() => expandedColumnDefs, [expandedColumnDefs]);

  return (
    <>
      <Grid container alignItems='center' className="table-with-accordion">
        <Grid item xs={12} sm={12}>
          <div className="w-100">
            {/* Main Header */}
            {showMainHeader && <Box className="cus-table-header" style={{ borderRadius: 0 }}>
              {
                memoizedColumnDefs?.map((column, idx) => (
                  <Typography key={column.headerName || column.field || idx} className="cus-table-cell">
                    {column.headerName}
                  </Typography>
                ))
              }
            </Box>}

            {dataList?.map((data, index) => {
              const tableSpecificData = getTableSpecificData(data);

              return (
                !tableSpecificData?.childrenLength
                  ? <Accordion key={tableSpecificData?.rowId || index} className="accordion-container">
                    <AccordionSummary expandIcon={<span style={{ minWidth: "14px", minHeight: "1px" }} />}>
                      <GridRow data={data} columns={memoizedColumnDefs} />
                    </AccordionSummary>
                  </Accordion>
                  : <Accordion
                    key={tableSpecificData?.rowId || index}
                    className="accordion-container"
                    expanded={expanded === tableSpecificData?.rowId}
                    onChange={handleExpandRegion(tableSpecificData?.rowId)}>

                    {/* Accordion Header */}
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`${tableSpecificData?.rowId}-heading-content`}
                      id={`panelheading-${tableSpecificData?.rowId}`}
                    >
                      <GridRow data={data} columns={memoizedColumnDefs} />
                    </AccordionSummary>

                    {/* Accordion body */}
                    <AccordionDetails>
                      <div
                        id={`scrollableDiv-${tableSpecificData?.rowId}`}
                        className="cus-ag-grid"
                        style={{ height: "400px", width: "100%", overflowY: "auto" }}
                      >
                        {/* Child table header */}
                        <Box className="cus-table-header" style={{ borderRadius: 0 }}>
                          {
                            memoizedExpandedColumnDefs?.map((column, idx) => {
                              let { style = {}, minWidth, flex, headerName, field, selectedAll } = column || {};

                              if (minWidth) {
                                style.minWidth = minWidth;
                              }
                              if (flex) {
                                style.flex = flex;
                              }

                              return (
                                <>
                                  {/* Checkbox to select all */}
                                  {selectedAll && <Box className="form-check px-1 pb-2 d-flex align-item-center gap-2 text-14">
                                    <input
                                      className="form-check-input cur-pointer"
                                      type="checkbox"
                                      id={`flexCheckIndeterminate`}
                                      checked={isSelectedAll}
                                      onChange={e => handleSelectAll(e, tableSpecificData.rowId)}
                                    />
                                  </Box>}
                                  <Typography key={headerName || field || idx} className="cus-table-cell" style={{ ...style }}>
                                    {headerName}
                                  </Typography>
                                </>
                              )
                            })
                          }
                        </Box>

                        {/* Child table body */}
                        {
                          isLoading
                            ? <TableLoader />
                            : <InfiniteScroll
                              dataLength={childrenList?.data?.length || 0}
                              next={handleDataList}
                              hasMore={hasMore}
                              loader={<TableLoader />}
                              scrollableTarget={`scrollableDiv-${tableSpecificData?.rowId}`}
                            >
                              {
                                (childrenList?.data?.length || 0) > 0
                                  ? childrenList?.data?.map((product, idx) => (
                                    <Box key={(product?._id || product?.id || idx)} className="accordion-header cus-table-row" sx={{ width: "100%" }}>
                                      {
                                        memoizedExpandedColumnDefs?.map((column) => {
                                          let { style = {}, minWidth, flex, headerName, field, cellRenderer, valueGetter, checkboxSelection, setRowId } = column || {};
                                          const rowId = setRowId ? setRowId({ data: product }) : "";

                                          const cellValue = cellRenderer
                                            ? cellRenderer({ data: product, colDef: column })
                                            : valueGetter
                                              ? valueGetter({ data: product })
                                              : product[field];

                                          if (minWidth) {
                                            style.minWidth = minWidth;
                                          }
                                          if (flex) {
                                            style.flex = flex;
                                          }

                                          return (
                                            <>
                                              {/* Checkbox to select row */}
                                              {(() => {
                                                if (checkboxSelection && rowId) {
                                                  const isRowSelected = isSelectedAll || selectedRows?.some(row => row.rowId === rowId);

                                                  return (<Box className="form-check px-1 pb-2 d-flex align-item-center gap-2 text-14">
                                                    <input
                                                      className="form-check-input cur-pointer"
                                                      type="checkbox"
                                                      id={`flexCheckIndeterminate`}
                                                      name={rowId}
                                                      checked={isRowSelected}
                                                      onChange={e => handleRowSelection(e, rowId, tableSpecificData?.rowId, product)}
                                                    />
                                                  </Box>)
                                                }
                                              })()}

                                              {/* Cell data */}
                                              <Typography
                                                key={`${field || headerName}-${product?._id || product?.id || idx}`}
                                                className="cus-table-cell" style={{ ...style }}
                                              >
                                                {cellValue}
                                              </Typography>
                                            </>
                                          )
                                        })
                                      }
                                    </Box>
                                  ))
                                  : childrenList?.data?.length === 0
                                    ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                                      <NoDataFound />
                                    </div>
                                    : null
                              }
                            </InfiniteScroll>
                        }
                      </div>
                    </AccordionDetails>
                  </Accordion>
              )
            })}
          </div>
        </Grid>
      </Grid>
    </>
  )
}

export default AgGridAccordionChildScrollingTable